import { Link } from '@tanstack/react-router'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { COLORS, FONTS, EASE_OUT, NEUTRAL1_RGB, NEUTRAL9_RGB } from '../home/tokens'
import { CtaButton } from '../home/CtaButton'
import { useIsMobile } from '@/lib/hooks'

/**
 * Bottom consent bar. Shown until the visitor makes a choice. Offers
 * Accept all / Reject all / Manage preferences. Sits below the header (z 100)
 * and mobile menu (z 99) at z 95.
 */
export function CookieConsentBanner({
  show,
  onAcceptAll,
  onRejectAll,
  onManage,
}: {
  show: boolean
  onAcceptAll: () => void
  onRejectAll: () => void
  onManage: () => void
}) {
  const reduce = useReducedMotion()
  const isMobile = useIsMobile()

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduce ? 0 : 24 }}
          transition={reduce ? { duration: 0 } : { duration: 0.45, ease: EASE_OUT }}
          role="dialog"
          aria-label="Cookie consent"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 95,
            display: 'flex',
            justifyContent: 'center',
            padding: 'clamp(12px, 2vw, 24px)',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              pointerEvents: 'auto',
              width: '100%',
              maxWidth: 1100,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? 16 : 28,
              backgroundColor: COLORS.neutral8,
              border: `1px solid rgba(${NEUTRAL1_RGB},0.08)`,
              borderRadius: 18,
              boxShadow: `0 16px 48px rgba(${NEUTRAL9_RGB},0.4)`,
              padding: 'clamp(18px, 2.4vw, 26px)',
              boxSizing: 'border-box',
            }}
          >
            <p
              style={{
                flex: 1,
                margin: 0,
                fontFamily: FONTS.body,
                fontSize: 'var(--text-sm)',
                lineHeight: 1.6,
                color: COLORS.neutral2,
              }}
            >
              We use cookies to run this site, measure usage, and support our
              forms. You can accept all, reject non-essential cookies, or choose
              by category. See our{' '}
              <Link
                to="/privacy/"
                style={{ color: COLORS.green1, textDecoration: 'underline' }}
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                flex: '0 0 auto',
              }}
            >
              <CtaButton label="Manage preferences" variant="ghost" onClick={onManage} />
              <CtaButton label="Reject all" variant="outline" onClick={onRejectAll} />
              <CtaButton label="Accept all" variant="solid" onClick={onAcceptAll} />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
